import React, { useEffect, useRef, useState } from 'react';
import { FaShieldAlt, FaSyncAlt } from 'react-icons/fa';
import axiosInstance from '../utility/axiosInstance';
import { BUDGET_MAX, BUDGET_MIN, BUDGET_REACHED_TITLE, BudgetMode, HARD_BUDGET_CONFIRM_WORD, budgetModeOf, budgetReachedMessage, confirmsHardBudget, money } from '../utility/budget';
import ConfirmationModal from './ConfirmationModal';
import { useToast } from './ToastContext';

export interface BudgetStatus {
    limit: number | null;
    spent: number;
    percentage: number;
    period_start: string;
    period_end: string;
    reached: boolean;
    enforcement_active?: boolean;
    /** The saved mode, on the GET and the PUT reply alike. Absent from a server that predates the modes: read it through budgetModeOf. */
    mode?: BudgetMode;
    // GET only: the PUT reply leaves them out (BUDGET_MANDATORY_PLAN_2026-10-28 sections 5.1 and 5.8).
    required?: boolean;
    credit_expires_at?: string | null;
    /** A trial's budget, fixed at its trial credit until a card is added (GET /v1/billing/budget). */
    trial_budget_gbp?: number | null;
    /** A plan account's credits for this period (GET /v1/billing/budget), which still pay usage at the budget's limit. */
    period_credits_remaining?: number;
}

/*
 * ⛔ The two modes, in the words of the signup budget page (keycloak-extend budget_setup.ftl). Milos, 2026-10-28: "Just 2
 * buttons. Save budget and continue and Save hard budget and continue", "explain in few words", and for the hard one
 * "As danger show: resources will be deleted [...] And ask to [confirm]", and to confirm it "I want users to type
 * something to confirm it": the danger box asks for IACCEPTTHEDELETIONRISK to be typed. Usage is evaluated hourly, so the danger box
 * says within about an hour, never the moment the budget is reached.
 */
const MODES: ReadonlyArray<{ mode: BudgetMode; title: string; note: string }> = [
    { mode: 'soft', title: 'Budget', note: 'Resources keep running and billing, but no new resources can be created.' },
    { mode: 'hard', title: 'Hard budget', note: 'Deletes all billable resources when the budget is reached.' },
];
const HARD_DANGER = 'All billable resources will be deleted within about an hour of reaching this budget, to stop further billing. This cannot be undone.';

// Rendered without a Redux store (its test does that), so the page passes in what the store knows.
interface BudgetSettingsProps {
    autoFocus?: boolean;
    /** The input took the focus autoFocus asked for, so the page can ask again (its Set budget button, clicked twice). */
    onFocused?: () => void;
    /** A budget was saved or removed: the PUT reply, so the page can clear the budget banner without a reload. */
    onSaved?: (status: BudgetStatus) => void;
    /** This account must have a budget: it can be changed, never removed (Milos, 2026-10-28; plan section 2.3). */
    required?: boolean;
    /** The credit this period's invoice will use (the forecast's credits_for_period), whether the forecast loaded, its tier. */
    creditBalance?: number | null;
    forecastLoaded?: boolean;
    tier?: string | null;
    /** The billing forecast's total for this month, for the budget line. */
    forecastTotal?: number | null;
    /**
     * A plan account (Regular, Professional, Enterprise): the same card and the same two budgets (Milos, 2026-11-01: "I want
     * the way I defined it before to work and look premium"), saved through the plan budget API, never below the plan's
     * commitment, and fixed until a card is added. On Professional and Enterprise (a commitment above £0) it is optional:
     * the commitment is a monthly minimum spend, never a cap ("They can spend as much as they want").
     */
    plan?: { minimum: number; cardless: boolean } | null;
}

/** "29 November": the budget's months are UTC months, so its dates are UTC dates. */
/**
 * ⛔ The budget as one line (Milos, 2026-10-29, after several drafts: of three layouts "all 3 do not work. Because part of
 * it or all of it is covered by credits", then of the two that show the credit "D is ok. If mouse over actually explain
 * (not in a wall of text) whats there. Do not say: Charged to card 50.00. It looks like it will be charged no matter
 * what. Remove that part"). The numbers first (used, estimated by the month's end, the limit), then one bar: the part the
 * credit pays shaded from its start (red on a hard budget, green otherwise) and named under it, what is used, the rest
 * of the estimate striped, and the end: a hard budget's red mark, a soft one's striped run past it. Each part says what
 * it is, in a few words, on mouse over or a tap, and is highlighted while it does.
 */
type BarPart = 'used' | 'estimate' | 'room' | 'over';

export function BudgetBar({ budgetPence, spentPence, forecastPence, creditPence, mode, what, status, resets, label, captionId }: {
    budgetPence: number; spentPence: number; forecastPence: number | null; creditPence: number; mode: BudgetMode;
    what: string; status?: string; resets?: string; label?: string; captionId?: string;
}) {
    const [tip, setTip] = useState<{ part: BarPart; at: number; byCredit: boolean } | null>(null);
    const hard = mode === 'hard';
    const estimate = Math.max(spentPence, forecastPence ?? 0);
    const tail = hard ? 0 : Math.max(estimate - budgetPence, budgetPence * 0.12);
    const total = budgetPence + tail;
    const at = (pence: number) => (Math.min(Math.max(pence, 0), total) / total) * 100;
    const pct = (pence: number) => `${at(pence)}%`;
    const used = Math.min(spentPence, budgetPence);
    const projected = Math.min(estimate, budgetPence);
    // The credit this period's invoice will use (the forecast's credits_for_period, by Stripe's rules for a card account),
    // paying the period's first usage, so its part is the start of the budget.
    const credit = Math.min(Math.max(creditPence, 0), budgetPence);
    const by = resets ? `by ${resets}` : 'this month';
    const explain: Record<BarPart, string> = {
        used: `Used so far: ${money(spentPence / 100)}`,
        estimate: `Estimated ${by}: ${money(estimate / 100)}`,
        room: hard ? `Room before the limit: ${money((budgetPence - projected) / 100)}` : `Room left in the budget: ${money((budgetPence - projected) / 100)}`,
        over: 'Past the budget, running resources keep billing',
    };
    const point = (event: React.MouseEvent<HTMLDivElement>) => {
        const box = event.currentTarget.getBoundingClientRect();
        const share = box.width > 0 ? Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1) : 0;
        const pence = share * total;
        const part: BarPart = used > 0 && pence <= used ? 'used' : projected > used && pence <= projected ? 'estimate'
            : pence <= budgetPence ? 'room' : 'over';
        setTip({ part, at: share * 100, byCredit: part !== 'over' && pence <= credit });
    };
    const on = (part: BarPart) => (tip?.part === part ? ' budget-bar__seg--on' : '');
    const side = tip && tip.at < 20 ? ' budget-bar__tip--start' : tip && tip.at > 80 ? ' budget-bar__tip--end' : '';
    return (
        <div className={`budget-bar budget-bar--${mode}`} aria-label={label}>
            <div className="budget-bar__stats">
                <div><span>Used</span><strong>{money(spentPence / 100)}</strong></div>
                <div><span>{`Estimated ${by}`}</span><strong>{money(estimate / 100)}</strong></div>
                <div className="budget-bar__limit"><span>{hard ? 'Limit' : 'Budget'}</span><strong>{money(budgetPence / 100)}</strong></div>
            </div>
            <div className="budget-bar__lines" onMouseLeave={() => setTip(null)}>
                <div className="budget-bar__hit" onMouseMove={point} onClick={point}>
                    <div className="budget-bar__track">
                        {credit > 0 && <span className="budget-bar__credit" style={{ width: pct(credit) }} />}
                        {used > 0 && <span className={`budget-bar__spent${on('used')}`} style={{ width: pct(used) }} />}
                        {projected > used && <span className={`budget-bar__forecast${on('estimate')}`} style={{ left: pct(used), width: pct(projected - used) }} />}
                        {budgetPence > projected && <span className={`budget-bar__room${on('room')}`} style={{ left: pct(projected), width: pct(budgetPence - projected) }} />}
                        {tail > 0 && <span className={`budget-bar__over${on('over')}`} style={{ left: pct(budgetPence), width: pct(tail) }} />}
                    </div>
                    {hard && <span className="budget-bar__end" style={{ left: pct(budgetPence) }} />}
                </div>
                {tip && (
                    <div role="tooltip" className={`budget-bar__tip${side}`} style={{ left: `${tip.at}%` }}>
                        <span>{explain[tip.part]}</span>
                        {tip.byCredit && <span className="budget-bar__tip-credit">Paid by your credit</span>}
                    </div>
                )}
            </div>
            {credit > 0 && <p className="budget-bar__paid">{`Paid by credit ${money(credit / 100)}`}</p>}
            <p className="budget-bar__what" id={captionId}>{what}</p>
            {status && <p className="budget-bar__status">{status}</p>}
        </div>
    );
}

const BudgetSettings: React.FC<BudgetSettingsProps> = ({
    autoFocus = false, onFocused, onSaved, required = false, creditBalance = null, forecastLoaded = false, tier = null, forecastTotal = null,
    plan = null,
}) => {
    const { showToast } = useToast();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const hardConfirmRef = useRef<HTMLInputElement | null>(null);
    const [budget, setBudget] = useState<BudgetStatus | null>(null);
    const [value, setValue] = useState('');
    // The mode chosen, which starts as the saved one, and what is typed in the danger box. ⛔ A hard budget is saved only
    // once IACCEPTTHEDELETIONRISK is typed, and every hard save needs its own: a save or a change of mode clears it.
    const [chosenMode, setMode] = useState<BudgetMode>('soft');
    // ⛔ A trial or no-card account's budget is always hard (Milos, 2026-10-29: "Budget will not be optional anymore. For those that are trial or no card. Its always a hard budget"):
    // no choice and nothing to type; the API saves it hard.
    // A plan account without a card keeps the budget its plan set (£0 on Regular: free tier only) until a card is added.
    const trialOnly = !plan && tier === 'trial_no_card';
    const planFixed = !!plan?.cardless;
    const minimum = plan ? plan.minimum : BUDGET_MIN;
    const mode: BudgetMode = trialOnly ? 'hard' : chosenMode;
    const [hardConfirmText, setHardConfirmText] = useState('');
    const hardConfirmed = confirmsHardBudget(hardConfirmText);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pendingLimit, setPendingLimit] = useState<number | null>(null);
    // The latest callback, kept out of the focus effect's dependencies: the page passes a new one on every render, and
    // the field takes the focus only when autoFocus turns on or the load ends, never on a re-render of the page.
    const onFocusedRef = useRef(onFocused);
    onFocusedRef.current = onFocused;

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axiosInstance.get<BudgetStatus>('/v1/billing/budget');
            setBudget(data);
            setValue(data.limit === null ? '' : String(data.limit));
            setMode(budgetModeOf(data.mode));
        } catch {
            showToast('Failed to load your budget', 'error');
        } finally {
            setLoading(false);
        }
    };

    const chooseMode = (next: BudgetMode) => {
        setMode(next);
        setHardConfirmText('');
    };

    useEffect(() => { void load(); }, []);
    useEffect(() => {
        if (!autoFocus || loading) return;
        inputRef.current?.focus();
        onFocusedRef.current?.();
    }, [autoFocus, loading]);

    // A plan budget is saved through the plan API: a budget directly, a hard one through its deletion preview, confirmed by the
    // phrase typed above it, and a removed one (Professional and Enterprise) as no budget. Then the card reads the budget back,
    // in the shape it reads at load.
    const persistPlan = async (limit: number | null, asked: BudgetMode): Promise<BudgetStatus> => {
        if (limit !== null && asked === 'hard') {
            const { data: preview } = await axiosInstance.post('/v1/billing/plan-budget/preview', { limit, mode: 'hard' });
            await axiosInstance.put('/v1/billing/plan-budget', { limit, mode: 'hard', preview_token: preview.preview_token, confirm_deletion: true });
        } else {
            await axiosInstance.put('/v1/billing/plan-budget', { limit, mode: 'soft' });
        }
        window.dispatchEvent(new Event('registration-account-changed'));
        return (await axiosInstance.get<BudgetStatus>('/v1/billing/budget')).data;
    };

    const persist = async (limit: number | null) => {
        setSaving(true);
        try {
            // A removed budget is soft whatever is chosen (the API stores it so), so Remove names no mode. ⛔ A hard save
            // carries the typed confirmation, which the API asks for on every one (hard_budget_confirmation_required).
            const asked = limit === null ? 'soft' : mode;
            const body = limit === null || trialOnly ? { limit }
                : asked === 'hard' ? { limit, mode: asked, hard_confirm: HARD_BUDGET_CONFIRM_WORD } : { limit, mode: asked };
            const data = plan ? await persistPlan(limit, asked)
                : (await axiosInstance.put<BudgetStatus>('/v1/billing/budget', body)).data;
            // The reply names the mode the server saved, and that is the one shown: a server that predates the modes
            // drops the field and saves soft.
            const saved = budgetModeOf(data.mode);
            // Merged: the PUT reply has no required and no credit expiry, and a save changes neither.
            setBudget((previous) => ({ ...previous, ...data, mode: saved }));
            setValue(data.limit === null ? '' : String(data.limit));
            setMode(saved);
            setHardConfirmText('');
            if (asked === 'hard' && saved !== 'hard') showToast('Your budget was saved, but not as a hard budget. Please try again.', 'error');
            else showToast(limit === null ? 'Monthly budget removed' : saved === 'hard' ? 'Hard budget saved' : 'Monthly budget saved', 'success');
            onSaved?.({ ...data, mode: saved });
        } catch (error: any) {
            // A plan's refusal says why (below the plan's commitment, a stale preview), so it is shown as it is.
            showToast((plan && error?.response?.data?.error?.message) || 'Failed to update your budget', 'error');
        } finally {
            setSaving(false);
            setPendingLimit(null);
        }
    };

    /*
     * Whether a budget of `limit` is already reached, as the API decides it (registrationPolicy budgetReached). On a plan
     * account the spend includes the commitment, whose credits still pay usage: a budget equal to it, the lowest a person
     * can set, is reached only once this period's credits are used up, and Regular's £0 always is (free tier only).
     */
    const reachedAt = (current: BudgetStatus, limit: number) => {
        if (!plan) return limit <= current.spent;
        if (plan.minimum === 0 && limit === 0) return true;
        const limitPence = Math.round(limit * 100);
        const spentPence = Math.round(current.spent * 100);
        return limitPence < spentPence || (limitPence === spentPence && (current.period_credits_remaining ?? 0) <= 0);
    };

    const submit = (event: React.FormEvent) => {
        event.preventDefault();
        if (planFixed) return;
        // A trial saves its fixed budget, its trial credit; a hard one at or below the spend asks first, as below.
        if (trialOnly) {
            const fixed = budget?.trial_budget_gbp;
            if (fixed == null) return;
            if (budget && fixed <= budget.spent) setPendingLimit(fixed);
            else void persist(fixed);
            return;
        }
        const limit = Number(value);
        if (!Number.isFinite(limit) || limit < minimum || limit > BUDGET_MAX) {
            showToast(minimum > 0 ? `Enter a budget between ${money(minimum)} and £1,000,000` : 'Enter a budget between £0 and £1,000,000', 'error');
            return;
        }
        // ⛔ The button waits for IACCEPTTHEDELETIONRISK too; this also holds for Enter in a field.
        if (mode === 'hard' && !trialOnly && !hardConfirmed) {
            hardConfirmRef.current?.focus();
            return;
        }
        // A soft £0 budget is reached by design (free tier only), so it is not asked about while nothing is used; with usage
        // it is, to say that usage is still billed. A hard one deletes, so it always is.
        if (budget && reachedAt(budget, limit) && !(limit === 0 && mode === 'soft' && budget.spent === 0)) {
            setPendingLimit(limit);
            return;
        }
        void persist(limit);
    };

    // Either source can say so: the page's budget state, or this card's own read of the same endpoint. Never on Professional
    // or Enterprise, where the budget is optional (the banner still asks once a card is on, budgetController).
    const optionalPlanBudget = !!plan && plan.minimum > 0;
    const mustHaveBudget = !optionalPlanBudget && (required || budget?.required === true);
    const periodEnd = budget ? new Date(budget.period_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';

    // ⛔ No credit line while the forecast is loading or after it failed: a missing line, never a wrong number.
    const creditKnown = forecastLoaded && typeof creditBalance === 'number' && Number.isFinite(creditBalance);
    const balancePence = creditKnown ? Math.max(0, Math.round((creditBalance as number) * 100)) : 0;
    // The amount in the field, as it is typed, when the budget would accept it.
    const typed = value.trim() === '' ? NaN : Number(value);
    const typedPence = Number.isFinite(typed) && typed >= BUDGET_MIN && typed <= BUDGET_MAX ? Math.round(typed * 100) : null;
    // A trial's budget is its trial credit, fixed until a card is added (Milos, 2026-10-29: "Hard place montly budget to 5
    // (credits)... You can change ti after adding credit card").
    const fixedLimit = trialOnly ? (budget?.trial_budget_gbp ?? null) : null;
    const savedPence = budget?.limit != null ? Math.round(budget.limit * 100) : null;
    // A trial's saved budget counts only as the one it must be, hard at its trial credit. One saved before that rule (the
    // console took any amount in either mode) gets the button, which saves the fixed one.
    const trialSaved = fixedLimit !== null && savedPence === Math.round(fixedLimit * 100) && budget?.mode === 'hard';
    // The amount the line shows: a trial's fixed one, else the one being typed, else the saved budget.
    const barPence = fixedLimit !== null ? Math.round(fixedLimit * 100) : (typedPence ?? savedPence);
    const forecastKnown = forecastLoaded && typeof forecastTotal === 'number' && Number.isFinite(forecastTotal);
    const showsSaved = savedPence !== null && savedPence > 0 && barPence === savedPence && (!trialOnly || trialSaved);
    // Only a status that asks for something; the spend and the reset date are above the line.
    const savedStatus = showsSaved && budget ? (!budget.enforcement_active ? 'Update this budget to enable enforcement.'
        : budget.reached ? 'Limit reached. You cannot create new resources until you raise the budget or the month resets.'
        : undefined) : undefined;
    // A working budget needs no "Required." under the title (Milos, 2026-10-29: "Threre is just so much text").
    const hasBudget = trialOnly ? trialSaved : savedPence !== null && budget?.enforcement_active === true;
    // One short line under it. Choosing a hard budget on a card account is the moment of confirming it, next to the phrase,
    // so the full warning stays there; a trial's (never chosen) is short, its full warning on mouse over.
    const limitText = barPence !== null ? money(barPence / 100) : '';
    const what = mode === 'hard' ? (trialOnly ? `Reaching ${limitText} deletes all billable resources. Add a card to change it.` : HARD_DANGER)
        : `Reaching ${limitText} blocks new resources. Running ones keep billing.`;
    const barShown = barPence !== null && barPence > 0;

    return <>
        <section className={`budget-settings${budget?.reached && budget.limit !== 0 ? ' budget-settings--reached' : ''}`} aria-labelledby="budget-title">
            <div className="budget-settings__heading">
                <span className="budget-settings__icon"><FaShieldAlt /></span>
                <div>
                    <h3 id="budget-title">Monthly safety budget</h3>
                    {/* Milos, 2026-10-28: "You again went into overexplaining. Remove that text." What each mode does is its own line below. */}
                    {!hasBudget && <p>{mustHaveBudget ? (trialOnly ? 'Required.' : 'Required for card accounts.') : 'Optional.'}</p>}
                </div>
                {!loading && barShown && <span className={`budget-settings__pill budget-settings__pill--${mode}`}>{mode === 'hard' ? 'Hard limit' : 'Budget'}</span>}
            </div>

            {loading ? <div className="billing-inline-loading"><FaSyncAlt className="spin" /> Loading budget...</div> : <>
                {budget?.limit === 0 && <p className="budget-settings__credit">£0: free tier only. Paid resources cannot be created.</p>}
                {barShown && (
                    <BudgetBar
                        budgetPence={barPence as number}
                        spentPence={Math.round((budget?.spent ?? 0) * 100)}
                        forecastPence={forecastKnown ? Math.round((forecastTotal as number) * 100) : null}
                        creditPence={balancePence}
                        mode={mode}
                        label={showsSaved && budget ? `${budget.percentage}% of monthly budget used` : undefined}
                        what={what}
                        status={savedStatus}
                        resets={periodEnd || undefined}
                        captionId={mode === 'hard' ? 'budget-danger-text' : undefined}
                    />
                )}

                {/* A plan account without a card cannot change its budget, so there is nothing to choose or type yet. */}
                {planFixed ? <p className="budget-settings__credit">{budget?.limit == null ? 'Add a card to set one.' : 'Add a card to change it.'}</p> :
                <form className="budget-settings__form" onSubmit={submit}>
                    {!trialOnly && <div className="budget-settings__modes" role="radiogroup" aria-label="Budget type">
                        {MODES.map(({ mode: option, title }) => (
                            <label key={option} className={`budget-settings__mode budget-settings__mode--${option}${mode === option ? ' budget-settings__mode--on' : ''}`}>
                                <input
                                    type="radio"
                                    name="budget-mode"
                                    value={option}
                                    checked={mode === option}
                                    onChange={() => chooseMode(option)}
                                    disabled={saving}
                                    aria-labelledby={`budget-mode-${option}`}
                                />
                                <span>
                                    <strong id={`budget-mode-${option}`}>{title}</strong>
                                </span>
                            </label>
                        ))}
                    </div>}

                    {mode === 'hard' && !trialOnly && (
                        <div className="budget-settings__danger">
                            {/* The line above says what a hard budget does; without an amount there is no line, so it is said here. */}
                            {!barShown && <p id="budget-danger-text">{HARD_DANGER}</p>}
                            {<>
                            <label htmlFor="budget-hard-confirm">{`Type ${HARD_BUDGET_CONFIRM_WORD} to confirm`}</label>
                            <input
                                ref={hardConfirmRef}
                                id="budget-hard-confirm"
                                type="text"
                                value={hardConfirmText}
                                onChange={(event) => setHardConfirmText(event.target.value)}
                                placeholder={HARD_BUDGET_CONFIRM_WORD}
                                autoComplete="off"
                                spellCheck={false}
                                disabled={saving}
                                aria-describedby="budget-danger-text"
                            />
                            </>}
                        </div>
                    )}

                    {trialOnly ? (!trialSaved && fixedLimit !== null && (
                        <div className="budget-settings__input-row">
                            <button type="submit" className="billing-danger-action" disabled={saving}>{saving ? 'Saving...' : 'Set budget'}</button>
                        </div>
                    )) : <>
                    <label htmlFor="monthly-budget">Monthly limit</label>
                    <div className="budget-settings__input-row">
                        <div className="budget-settings__amount">
                        <span aria-hidden="true">£</span>
                        <input
                            ref={inputRef}
                            id="monthly-budget"
                            type="number"
                            min={minimum}
                            max={BUDGET_MAX}
                            step="0.01"
                            inputMode="decimal"
                            value={value}
                            onChange={(event) => setValue(event.target.value)}
                            placeholder={balancePence > 0 ? (balancePence / 100).toFixed(2) : minimum > 0 ? minimum.toFixed(2) : '50.00'}
                            disabled={saving}
                        />
                        </div>
                        <button
                            type="submit"
                            className={mode === 'hard' ? 'billing-danger-action' : 'billing-primary-action'}
                            disabled={saving || (mode === 'hard' && !trialOnly && !hardConfirmed)}
                        >
                            {saving ? 'Saving...' : `${budget?.limit != null ? 'Update' : 'Set'} ${mode === 'hard' && !trialOnly ? 'hard budget' : 'budget'}`}
                        </button>
                        {/* Professional and Enterprise can go back to no budget, their default; Regular keeps one once a card is on. */}
                        {budget?.limit != null && !mustHaveBudget && (!plan || optionalPlanBudget) && <button type="button" className="billing-secondary-action" disabled={saving} onClick={() => void persist(null)}>Remove</button>}
                    </div>
                    </>}
                </form>}
            </>}
        </section>

        {/* A hard budget at or below the spend starts deleting on the save itself, so it asks in danger words. */}
        <ConfirmationModal
            isOpen={pendingLimit !== null}
            onClose={() => setPendingLimit(null)}
            onConfirm={() => { if (pendingLimit !== null) void persist(pendingLimit); }}
            title={BUDGET_REACHED_TITLE}
            message={budgetReachedMessage(budget?.spent ?? 0, pendingLimit ?? 0, mode, periodEnd || undefined)}
            confirmText={mode === 'hard' && !trialOnly ? 'Set hard budget' : 'Set budget'}
            cancelText="Cancel"
            type={mode === 'hard' ? 'danger' : 'warning'}
            isConfirming={saving}
        />
    </>;
};

export default BudgetSettings;
